import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from "./ui/Avatar";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/cards";
import PasswordChangeForm from './PasswordChangeForm';
import { LogOut, ArrowUp, ArrowDown, Search, Download, Mail, Calendar, RefreshCw, Eye, Settings, Users } from "lucide-react";
import Logo from './images/for light bg.svg';

function AdminDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [reportStatus, setReportStatus] = useState(null);
  const [reportStatusLoading, setReportStatusLoading] = useState(false);
  const [runReportsLoading, setRunReportsLoading] = useState(false);
  const [sendTestEmailLoading, setSendTestEmailLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const fetchReportStatus = async () => {
    try {
      setReportStatusLoading(true);
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/api/admin/report-status`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setReportStatus(data);
      }
    } catch (e) {
      console.error('Failed to fetch report status', e);
    } finally {
      setReportStatusLoading(false);
    }
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('adminToken');
        
        console.log('Fetching clients with token:', token ? 'Present' : 'Missing');
        
        const response = await fetch(`${API_URL}/api/admin/clients`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch clients: ${errorText}`);
        }

        const data = await response.json();
        console.log('Received clients data:', data);
        setClients(data);
      } catch (error) {
        console.error('Error fetching clients:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClients();
  }, [API_URL]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) fetchReportStatus();
  }, [API_URL]);

  const runMonthlyReportsNow = async () => {
    try {
      setRunReportsLoading(true);
      const res = await fetch(`${API_URL}/api/admin/run-monthly-reports`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setError(null);
        fetchReportStatus();
        alert(`Monthly reports run complete. Sent: ${data.sent || 0}, Failed: ${data.failed || 0}, Skipped: ${data.skipped || 0}`);
      } else {
        setError(data.message || 'Failed to run monthly reports');
      }
    } catch (e) {
      setError(e.message || 'Failed to run monthly reports');
    } finally {
      setRunReportsLoading(false);
    }
  };

  const sendTestReportEmail = async () => {
    if (!selectedClient?.['Id no']) return;
    try {
      setSendTestEmailLoading(true);
      const res = await fetch(`${API_URL}/api/admin/test-report-email/${selectedClient['Id no']}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({})
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setError(null);
        alert(`Test report email sent to client's email.`);
      } else {
        setError(data.error || data.message || 'Failed to send test email');
      }
    } catch (e) {
      setError(e.message || 'Failed to send test email');
    } finally {
      setSendTestEmailLoading(false);
    }
  };

  const getUniqueClients = (clients) => {
    if (!Array.isArray(clients)) return [];
    
    const uniqueMap = new Map();
    clients.forEach(client => {
      const id = client['Id no'] || client['ID'];
      if (id && !uniqueMap.has(id)) {
        uniqueMap.set(id, {
          ...client,
          investments: client.investments || []
        });
      }
    });
    return Array.from(uniqueMap.values());
  };

  const uniqueClients = getUniqueClients(clients);
  const searchLower = searchQuery.trim().toLowerCase();
  const filteredClients = searchLower
    ? uniqueClients.filter(client => {
        const idStr = (client?.['Id no']?.toString() || '').toLowerCase();
        const nameStr = (client?.Name || '').toLowerCase();
        return idStr.includes(searchLower) || nameStr.includes(searchLower);
      })
    : uniqueClients;

  const downloadReport = async (clientId) => {
    try {
      setIsDownloading(true);
      const response = await fetch(`${API_URL}/api/client/report/${clientId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${clientId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      setError('Failed to download report');
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadAllReports = async () => {
    try {
      setIsDownloading(true);
      const response = await fetch(`${API_URL}/api/admin/reports/bulk`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download reports');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'all-reports.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Bulk download failed:', error);
      setError('Failed to download reports');
    } finally {
      setIsDownloading(false);
    }
  };

  const calculateTotalValue = (investments) => {
    if (!Array.isArray(investments)) return 0;
    return investments.reduce((sum, inv) => {
      const value = typeof inv.currentValue === 'string' 
        ? parseFloat(inv.currentValue.replace(/[,$]/g, ''))
        : (inv.currentValue || 0);
      return sum + value;
    }, 0);
  };

  const calculateTotalInvestment = (investments) => {
    if (!Array.isArray(investments)) return 0;
    return investments.reduce((sum, inv) => {
      const amount = typeof inv.investmentAmount === 'string'
        ? parseFloat(inv.investmentAmount.replace(/[,$]/g, ''))
        : (inv.investmentAmount || 0);
      return sum + amount;
    }, 0);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0A0A0A]" style={{ fontFamily: 'Lexend, sans-serif' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#676DFF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white font-medium">Loading client data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0A0A0A] p-4" style={{ fontFamily: 'Lexend, sans-serif' }}>
        <div className="bg-[#151515] border border-white/10 rounded-xl p-6 max-w-md text-center">
          <p className="text-red-400 mb-4">Error: {error}</p>
          <Button variant="outline" onClick={() => window.location.reload()} className="border-white/20 text-white">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#0A0A0A] text-white" style={{ fontFamily: 'Lexend, sans-serif' }}>
      <aside className="w-64 bg-[#151515] border-r border-white/10 flex flex-col flex-shrink-0">
        <div className="p-6 flex flex-col items-center border-b border-white/10">
          <Avatar className="h-14 w-14">
            <AvatarImage src={Logo} alt="Logo" className="p-2" />
            <AvatarFallback><img src={Logo} alt="Logo" className="p-2" /></AvatarFallback>
          </Avatar>
          <h2 className="mt-3 text-lg font-semibold text-white">Admin Dashboard</h2>
        </div>

        <div className="p-3 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B8B8B8]" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#0F0F0F] border border-white/10 text-white placeholder:text-[#B8B8B8] focus:outline-none focus:border-[#676DFF] text-sm"
            />
          </div>
          <p className="text-xs text-[#B8B8B8] mt-1.5">
            {searchLower ? `${filteredClients.length} of ${uniqueClients.length} clients` : `${uniqueClients.length} clients`}
          </p>
        </div>

        <nav className="flex-1 overflow-y-auto min-h-0 p-2">
          {filteredClients.length === 0 ? (
            <div className="py-6 px-3 text-center text-[#B8B8B8] text-sm">
              {uniqueClients.length === 0 ? 'No clients yet' : searchLower ? `No matches for "${searchQuery.trim()}"` : 'No clients'}
            </div>
          ) : (
            filteredClients.map(client => (
              <div key={client['Id no']} className="flex items-center gap-1 mb-1">
                <Button
                  variant={selectedClient?.['Id no'] === client['Id no'] ? 'default' : 'ghost'}
                  className="flex-1 justify-start min-w-0 h-9 text-left text-sm text-white hover:bg-white/10"
                  onClick={() => setSelectedClient(client)}
                >
                  <span className="truncate">{client['Id no']} – {client?.Name || 'Unknown'}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-8 w-8 text-[#B8B8B8] hover:text-[#676DFF]"
                  onClick={(e) => { e.stopPropagation(); navigate(`/admin/preview/${client['Id no']}`); }}
                  title="View as user"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </nav>

        <div className="p-3 space-y-2 border-t border-white/10">
          <Button
            onClick={downloadAllReports}
            disabled={isDownloading}
            className="w-full justify-center bg-[#676DFF] hover:bg-[#5A60E8] text-white border-0"
          >
            <Download className="mr-2 h-4 w-4" />
            {isDownloading ? 'Downloading...' : 'Download All Reports'}
          </Button>
          <Button
            variant="outline"
            className="w-full justify-center border-white/20 text-[#B8B8B8] hover:bg-white/10 hover:text-white"
            onClick={() => setShowPasswordForm(!showPasswordForm)}
          >
            <Settings className="mr-2 h-4 w-4" />
            {showPasswordForm ? 'Hide settings' : 'Password & username'}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-center text-[#B8B8B8] hover:text-white hover:bg-white/10"
            onClick={onLogout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto p-6">
        <Card className="mb-6 bg-[#151515] border-white/10">
          <CardHeader className="flex flex-row items-center justify-between border-b border-white/10">
            <CardTitle className="flex items-center gap-2 text-white text-lg">
              <Calendar className="h-5 w-5 text-[#676DFF]" />
              Monthly Reports
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchReportStatus} disabled={reportStatusLoading} className="border-white/20 text-white hover:bg-white/10">
                <RefreshCw className={`mr-2 h-4 w-4 ${reportStatusLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button size="sm" onClick={runMonthlyReportsNow} disabled={runReportsLoading} className="bg-[#676DFF] hover:bg-[#5A60E8] text-white">
                {runReportsLoading ? 'Running...' : 'Run monthly reports now'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {reportStatusLoading && !reportStatus ? (
              <p className="text-sm text-[#B8B8B8]">Loading report status...</p>
            ) : reportStatus ? (
              <div className="space-y-2 text-sm text-[#B8B8B8]">
                <p><strong className="text-white">Schedule:</strong> {reportStatus.schedule}</p>
                <p><strong className="text-white">Email configured:</strong> {reportStatus.emailConfigured ? 'Yes' : 'No (set RESEND_API_KEY)'}</p>
                {reportStatus.lastReportMonth && <p><strong className="text-white">Last report month:</strong> {reportStatus.lastReportMonth}</p>}
                {reportStatus.summary && (
                  <p><strong className="text-white">Recent:</strong> {reportStatus.summary.sent} sent, {reportStatus.summary.failed} failed</p>
                )}
                {reportStatus.recentLogs?.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto rounded-lg border border-white/10 p-2 bg-[#0F0F0F]">
                    <p className="font-medium text-white mb-1 text-xs">Recent delivery log</p>
                    {reportStatus.recentLogs.slice(0, 10).map((log, i) => (
                      <div key={log.id || i} className="flex gap-2 text-xs">
                        <span>{log.report_month}</span>
                        <span>{log.client_id}</span>
                        <span className={log.status === 'sent' ? 'text-green-400' : 'text-red-400'}>{log.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-[#B8B8B8]">Report status not available. Run the migration for monthly_report_logs if needed.</p>
            )}
          </CardContent>
        </Card>

        {showPasswordForm ? (
          <div className="mb-6">
            <PasswordChangeForm />
          </div>
        ) : null}

        {selectedClient ? (
          <div className="space-y-6">
            <Card className="bg-[#151515] border-white/10">
              <CardHeader className="flex flex-row items-center justify-between border-b border-white/10">
                <CardTitle className="text-white text-lg">{selectedClient.Name}'s Portfolio Overview</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => navigate(`/admin/preview/${selectedClient['Id no']}`)} className="bg-[#676DFF] hover:bg-[#5A60E8] text-white border-0">
                    <Eye className="mr-2 h-4 w-4" />
                    View as user
                  </Button>
                  <Button variant="outline" onClick={() => downloadReport(selectedClient['Id no'])} disabled={isDownloading} className="border-white/20 text-white hover:bg-white/10">
                    <Download className="mr-2 h-4 w-4" />
                    {isDownloading ? 'Downloading...' : 'Download Report'}
                  </Button>
                  <Button variant="outline" onClick={sendTestReportEmail} disabled={sendTestEmailLoading} className="border-white/20 text-white hover:bg-white/10">
                    <Mail className="mr-2 h-4 w-4" />
                    {sendTestEmailLoading ? 'Sending...' : 'Send test report email'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="text-white">
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-[#B8B8B8]">Total Investment</p>
                    <p className="text-xl font-bold text-white">
                      ${calculateTotalInvestment(selectedClient.investments).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#B8B8B8]">Current Value</p>
                    <p className="text-xl font-bold text-white">
                      ${calculateTotalValue(selectedClient.investments).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#B8B8B8]">Total Return</p>
                    <div className="flex items-center">
                      {calculateTotalValue(selectedClient.investments) >= calculateTotalInvestment(selectedClient.investments) ? (
                        <ArrowUp className="h-5 w-5 text-green-500 mr-1" />
                      ) : (
                        <ArrowDown className="h-5 w-5 text-red-500 mr-1" />
                      )}
                      <p className="text-xl font-bold text-white">
                        {(((calculateTotalValue(selectedClient.investments) - calculateTotalInvestment(selectedClient.investments)) / 
                          (calculateTotalInvestment(selectedClient.investments) || 1)) * 100).toFixed(2)}%
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {selectedClient.investments?.map((investment, index) => (
                <Card key={`${investment.portfolioType}-${index}`} className="bg-[#151515] border-white/10">
                  <CardHeader className="border-b border-white/10">
                    <CardTitle className="text-white">{investment.portfolioType}</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><p className="text-[#B8B8B8]">Investment Amount</p><p className="font-semibold text-white">${parseFloat(investment.investmentAmount || 0).toFixed(2)}</p></div>
                      <div><p className="text-[#B8B8B8]">Current Value</p><p className="font-semibold text-white">${parseFloat(investment.currentValue || 0).toFixed(2)}</p></div>
                      <div><p className="text-[#B8B8B8]">NAV</p><p className="font-semibold text-white">${parseFloat(investment.entryPrice || 0).toFixed(4)}</p></div>
                      <div><p className="text-[#B8B8B8]">HWM</p><p className="font-semibold text-white">${parseFloat(investment.hwm || 0).toFixed(2)}</p></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#676DFF]/20 flex items-center justify-center mb-4">
              <Users className="h-8 w-8 text-[#676DFF]" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Select a client</h3>
            <p className="text-[#B8B8B8] max-w-sm">Choose a client from the sidebar to view their portfolio, or use “View as user” to see exactly what they see.</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminDashboard;